import { useState, useEffect, useRef } from "react";
import api from "@/lib/api";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { CompanyLogo } from "@/components/CompanyLogo";
import { Plus, PencilSimple, Upload, Users, CurrencyDollar, Brain, Envelope, ArrowRight } from "@phosphor-icons/react";
import { toast } from "sonner";

const PLAN_BADGE = {
  pyme: "bg-slate-600 text-white",
  equipo: "bg-teal-600 text-white",
  profesional: "bg-[#002FA7] text-white",
  corporativo: "bg-amber-600 text-white",
};

const EMPTY_FORM = { company_name: "", contact_name: "", contact_email: "", contact_phone: "", plan_id: "pyme", billing_cycle: "monthly", from_lead_id: null, source_lead_email: null };

export default function AdminClientsPage() {
  const [accounts, setAccounts] = useState([]);
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("clients");
  const [leadsSubTab, setLeadsSubTab] = useState("pending");
  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const fileRef = useRef(null);
  const [logoTarget, setLogoTarget] = useState(null);

  const [form, setForm] = useState(EMPTY_FORM);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [a, s, p, l] = await Promise.all([
        api.get("/admin/clients"),
        api.get("/admin/stats"),
        api.get("/plans"),
        api.get("/admin/leads"),
      ]);
      setAccounts(a.data.accounts || []);
      setStats(s.data);
      setPlans(p.data.plans || []);
      setLeads(l.data.leads || []);
    } catch {
      toast.error("Error al cargar clientes");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  const handleCreate = async () => {
    if (!form.company_name || !form.contact_name || !form.contact_email) {
      toast.error("Nombre, contacto y email son requeridos");
      return;
    }
    try {
      const res = await api.post("/admin/clients", form);
      toast.success("Cliente creado", { description: `Contraseña temporal enviada por email: ${res.data.temp_password}` });
      setCreateOpen(false);
      setForm({ ...EMPTY_FORM });
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al crear cliente");
    }
  };

  const handleConvertLead = (lead) => {
    setForm({
      company_name: lead.company || "",
      contact_name: lead.name || "",
      contact_email: lead.email || "",
      contact_phone: lead.phone || "",
      plan_id: lead.plan || "pyme",
      billing_cycle: lead.billing_cycle || "monthly",
      from_lead_id: lead.id,
      source_lead_email: lead.email,
    });
    setCreateOpen(true);
  };

  const handleEdit = async () => {
    if (!editing) return;
    try {
      await api.put(`/admin/clients/${editing.id}`, {
        plan_id: editing.plan_id,
        billing_cycle: editing.billing_cycle,
        is_active: editing.is_active,
      });
      toast.success("Cliente actualizado");
      setEditOpen(false);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al actualizar");
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !logoTarget) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/admin/clients/${logoTarget}/logo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Logo actualizado");
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al subir logo");
    } finally {
      if (fileRef.current) fileRef.current.value = "";
      setLogoTarget(null);
    }
  };

  const fmtDate = (d) => d ? new Date(d).toLocaleDateString("es-GT", { year: "numeric", month: "short", day: "numeric" }) : "—";

  return (
    <div className="space-y-6" data-testid="admin-clients-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-heading font-black text-3xl tracking-tight">Clientes</h1>
          <p className="text-sm text-muted-foreground font-mono mt-1">Panel de administración de LicitaAI</p>
        </div>
        <Button onClick={() => { setForm({ ...EMPTY_FORM }); setCreateOpen(true); }} className="rounded-none bg-[#002FA7] hover:bg-[#001D66]" data-testid="create-client-btn">
          <Plus size={16} className="mr-2" /> Nuevo cliente
        </Button>
      </div>

      {/* KPIs */}
      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="rounded-none"><CardContent className="p-4">
            <div className="flex items-center gap-2 text-xs font-mono uppercase text-muted-foreground mb-2"><Users size={14}/> Activos</div>
            <p className="text-3xl font-heading font-black">{stats.total_active_clients}</p>
          </CardContent></Card>
          <Card className="rounded-none"><CardContent className="p-4">
            <div className="flex items-center gap-2 text-xs font-mono uppercase text-muted-foreground mb-2"><CurrencyDollar size={14}/> Ingresos/mes</div>
            <p className="text-3xl font-heading font-black">Q{stats.monthly_revenue_estimate?.toLocaleString()}</p>
          </CardContent></Card>
          <Card className="rounded-none"><CardContent className="p-4">
            <div className="flex items-center gap-2 text-xs font-mono uppercase text-muted-foreground mb-2"><Brain size={14}/> Análisis IA mes</div>
            <p className="text-3xl font-heading font-black">{stats.ai_analyses_this_month}</p>
          </CardContent></Card>
          <Card className="rounded-none"><CardContent className="p-4">
            <div className="flex items-center gap-2 text-xs font-mono uppercase text-muted-foreground mb-2"><Envelope size={14}/> Leads pendientes</div>
            <p className="text-3xl font-heading font-black">{stats.pending_leads}</p>
          </CardContent></Card>
        </div>
      )}

      {/* Tabs: Clients vs Pending leads */}
      <Tabs value={tab} onValueChange={setTab} className="w-full">
        <TabsList className="rounded-none bg-secondary h-11" data-testid="admin-tabs">
          <TabsTrigger value="clients" className="rounded-none font-medium data-[state=active]:bg-[#002FA7] data-[state=active]:text-white px-5" data-testid="tab-clients">
            Clientes activos
          </TabsTrigger>
          <TabsTrigger value="leads" className="rounded-none font-medium data-[state=active]:bg-[#002FA7] data-[state=active]:text-white px-5" data-testid="tab-leads">
            Prospectos pendientes
            {leads.filter(l => l.status === "pending").length > 0 && (
              <Badge className="ml-2 rounded-none bg-amber-500 text-white text-[10px] font-mono px-1.5 py-0">
                {leads.filter(l => l.status === "pending").length}
              </Badge>
            )}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="clients" className="mt-4">
          {/* Table */}
          <Card className="rounded-none">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-16"></TableHead>
              <TableHead className="font-mono text-xs uppercase">Empresa</TableHead>
              <TableHead className="font-mono text-xs uppercase">Contacto</TableHead>
              <TableHead className="font-mono text-xs uppercase">Plan</TableHead>
              <TableHead className="font-mono text-xs uppercase">Ciclo</TableHead>
              <TableHead className="font-mono text-xs uppercase">Usuarios</TableHead>
              <TableHead className="font-mono text-xs uppercase">Renovación</TableHead>
              <TableHead className="font-mono text-xs uppercase">Estado</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={9} className="text-center py-12 text-muted-foreground">Cargando...</TableCell></TableRow>
            ) : accounts.length === 0 ? (
              <TableRow><TableCell colSpan={9} className="text-center py-12 text-muted-foreground">Aún no hay clientes. Crea el primero.</TableCell></TableRow>
            ) : accounts.map((a) => (
              <TableRow key={a.id} data-testid={`client-row-${a.id}`}>
                <TableCell><CompanyLogo logoUrl={a.logo_url} companyName={a.company_name} size={36} /></TableCell>
                <TableCell>
                  <div className="font-medium">{a.company_name}</div>
                  {a.is_internal && <span className="text-[10px] font-mono text-muted-foreground">INTERNO</span>}
                </TableCell>
                <TableCell>
                  <div className="text-sm">{a.contact_name}</div>
                  <div className="text-xs font-mono text-muted-foreground">{a.contact_email}</div>
                </TableCell>
                <TableCell><Badge className={`rounded-none font-mono text-[10px] ${PLAN_BADGE[a.plan_id] || ""}`}>{a.plan_id.toUpperCase()}</Badge></TableCell>
                <TableCell className="text-xs font-mono">{a.billing_cycle === "annual" ? "Anual" : "Mensual"}</TableCell>
                <TableCell className="text-sm font-mono">{a.user_count || 0}</TableCell>
                <TableCell className="text-xs font-mono">{fmtDate(a.renewal_date)}</TableCell>
                <TableCell>
                  <Badge className={`rounded-none font-mono text-[10px] ${a.is_active ? "bg-[#2A9D8F] text-white" : "bg-secondary"}`}>
                    {a.is_active ? "ACTIVO" : "INACTIVO"}
                  </Badge>
                </TableCell>
                <TableCell>
                  <div className="flex gap-1">
                    <Button size="sm" variant="ghost" className="rounded-none h-8 w-8 p-0"
                      onClick={() => { setLogoTarget(a.id); setTimeout(() => fileRef.current?.click(), 50); }}
                      data-testid={`upload-logo-${a.id}`}>
                      <Upload size={14} />
                    </Button>
                    <Button size="sm" variant="ghost" className="rounded-none h-8 w-8 p-0"
                      onClick={() => { setEditing({...a}); setEditOpen(true); }}
                      data-testid={`edit-client-${a.id}`}>
                      <PencilSimple size={14} />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
        </TabsContent>

        <TabsContent value="leads" className="mt-4">
          <Tabs value={leadsSubTab} onValueChange={setLeadsSubTab} className="w-full">
            <TabsList className="rounded-none bg-secondary h-9 mb-3" data-testid="leads-subtabs">
              <TabsTrigger value="pending" className="rounded-none font-medium data-[state=active]:bg-[#002FA7] data-[state=active]:text-white px-4 text-xs" data-testid="subtab-pending">
                Pendientes ({leads.filter(l => l.status === "pending").length})
              </TabsTrigger>
              <TabsTrigger value="converted" className="rounded-none font-medium data-[state=active]:bg-[#2A9D8F] data-[state=active]:text-white px-4 text-xs" data-testid="subtab-converted">
                Convertidos ({leads.filter(l => l.status === "converted").length})
              </TabsTrigger>
            </TabsList>

            <TabsContent value="pending">
              <Card className="rounded-none" data-testid="leads-panel">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="font-mono text-xs uppercase">Fecha</TableHead>
                      <TableHead className="font-mono text-xs uppercase">Contacto</TableHead>
                      <TableHead className="font-mono text-xs uppercase">Empresa</TableHead>
                      <TableHead className="font-mono text-xs uppercase">Email / Teléfono</TableHead>
                      <TableHead className="font-mono text-xs uppercase">Plan</TableHead>
                      <TableHead className="font-mono text-xs uppercase">Ciclo</TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {loading ? (
                      <TableRow><TableCell colSpan={7} className="text-center py-12 text-muted-foreground">Cargando...</TableCell></TableRow>
                    ) : leads.filter(l => l.status === "pending").length === 0 ? (
                      <TableRow><TableCell colSpan={7} className="text-center py-12 text-muted-foreground">No hay prospectos pendientes de conversión.</TableCell></TableRow>
                    ) : leads.filter(l => l.status === "pending").map((l) => (
                      <TableRow key={l.id} data-testid={`lead-row-${l.id}`}>
                        <TableCell className="text-xs font-mono">{fmtDate(l.created_at)}</TableCell>
                        <TableCell className="text-sm font-medium">{l.name}</TableCell>
                        <TableCell className="text-sm">{l.company}</TableCell>
                        <TableCell>
                          <div className="text-xs font-mono">{l.email}</div>
                          <div className="text-xs font-mono text-muted-foreground">{l.phone}</div>
                        </TableCell>
                        <TableCell>
                          <Badge className={`rounded-none font-mono text-[10px] ${PLAN_BADGE[l.plan] || "bg-secondary"}`}>{(l.plan_name || l.plan || "").toUpperCase()}</Badge>
                        </TableCell>
                        <TableCell className="text-xs font-mono">{l.billing_cycle === "annual" ? "Anual" : "Mensual"}</TableCell>
                        <TableCell>
                          <Button size="sm" onClick={() => handleConvertLead(l)} className="rounded-none bg-[#002FA7] hover:bg-[#001D66] h-8 text-xs" data-testid={`convert-lead-${l.id}`}>
                            Convertir <ArrowRight size={12} className="ml-1.5" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Card>
            </TabsContent>

            <TabsContent value="converted">
              <Card className="rounded-none" data-testid="leads-converted-panel">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="font-mono text-xs uppercase">Solicitado</TableHead>
                      <TableHead className="font-mono text-xs uppercase">Convertido</TableHead>
                      <TableHead className="font-mono text-xs uppercase">Contacto</TableHead>
                      <TableHead className="font-mono text-xs uppercase">Empresa</TableHead>
                      <TableHead className="font-mono text-xs uppercase">Plan solicitado</TableHead>
                      <TableHead className="font-mono text-xs uppercase">Plan asignado</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {loading ? (
                      <TableRow><TableCell colSpan={6} className="text-center py-12 text-muted-foreground">Cargando...</TableCell></TableRow>
                    ) : leads.filter(l => l.status === "converted").length === 0 ? (
                      <TableRow><TableCell colSpan={6} className="text-center py-12 text-muted-foreground">Aún no hay prospectos convertidos.</TableCell></TableRow>
                    ) : leads.filter(l => l.status === "converted").map((l) => {
                      const acc = accounts.find(a => a.id === l.converted_account_id);
                      const assignedPlan = acc?.plan_id;
                      return (
                        <TableRow key={l.id} data-testid={`converted-lead-row-${l.id}`}>
                          <TableCell className="text-xs font-mono">{fmtDate(l.created_at)}</TableCell>
                          <TableCell className="text-xs font-mono text-[#2A9D8F]">{fmtDate(l.converted_at)}</TableCell>
                          <TableCell>
                            <div className="text-sm font-medium">{l.name}</div>
                            <div className="text-xs font-mono text-muted-foreground">{l.email}</div>
                          </TableCell>
                          <TableCell className="text-sm">{l.company}</TableCell>
                          <TableCell>
                            <Badge className={`rounded-none font-mono text-[10px] ${PLAN_BADGE[l.plan] || "bg-secondary"}`}>{(l.plan_name || l.plan || "").toUpperCase()}</Badge>
                          </TableCell>
                          <TableCell>
                            {assignedPlan ? (
                              <Badge className={`rounded-none font-mono text-[10px] ${PLAN_BADGE[assignedPlan] || "bg-secondary"}`}>{assignedPlan.toUpperCase()}</Badge>
                            ) : (
                              <span className="text-xs font-mono text-muted-foreground">—</span>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </Card>
            </TabsContent>
          </Tabs>
        </TabsContent>
      </Tabs>

      <input ref={fileRef} type="file" accept=".png,.jpg,.jpeg,.svg,.webp" onChange={handleLogoUpload} className="hidden" />

      {/* Create Dialog */}
      <Dialog open={createOpen} onOpenChange={(o) => { setCreateOpen(o); if (!o) setForm({ ...EMPTY_FORM }); }}>
        <DialogContent className="rounded-none max-w-xl">
          <DialogHeader>
            <DialogTitle className="font-heading">{form.from_lead_id ? "Convertir prospecto" : "Nuevo cliente"}</DialogTitle>
          </DialogHeader>
          {form.from_lead_id && (
            <div className="border-l-2 border-[#2A9D8F] bg-[#2A9D8F]/5 px-3 py-2 text-xs font-mono text-muted-foreground" data-testid="convert-source-banner">
              Conversión desde prospecto: <span className="text-foreground font-semibold">{form.source_lead_email}</span>. Revisa el plan antes de confirmar.
            </div>
          )}
          <div className="space-y-4 py-2">
            <div><Label className="text-xs font-mono uppercase">Empresa</Label>
              <Input value={form.company_name} onChange={(e) => setForm({...form, company_name: e.target.value})} className="rounded-none mt-1" data-testid="new-company"/>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label className="text-xs font-mono uppercase">Nombre del contacto</Label>
                <Input value={form.contact_name} onChange={(e) => setForm({...form, contact_name: e.target.value})} className="rounded-none mt-1" data-testid="new-contact-name"/>
              </div>
              <div><Label className="text-xs font-mono uppercase">Teléfono</Label>
                <Input value={form.contact_phone} onChange={(e) => setForm({...form, contact_phone: e.target.value})} className="rounded-none mt-1" data-testid="new-phone"/>
              </div>
            </div>
            <div><Label className="text-xs font-mono uppercase">Email del contacto (será su usuario)</Label>
              <Input type="email" value={form.contact_email} onChange={(e) => setForm({...form, contact_email: e.target.value})} className="rounded-none mt-1" data-testid="new-email"/>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label className="text-xs font-mono uppercase">Plan</Label>
                <Select value={form.plan_id} onValueChange={(v) => setForm({...form, plan_id: v})}>
                  <SelectTrigger className="rounded-none mt-1" data-testid="new-plan"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {plans.map(p => <SelectItem key={p.id} value={p.id}>{p.name} · Q{p.price_monthly}/mes</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs font-mono uppercase">Ciclo</Label>
                <Select value={form.billing_cycle} onValueChange={(v) => setForm({...form, billing_cycle: v})}>
                  <SelectTrigger className="rounded-none mt-1" data-testid="new-billing"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="monthly">Mensual</SelectItem>
                    <SelectItem value="annual">Anual</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <p className="text-[11px] text-muted-foreground">Se enviará automáticamente un correo de bienvenida con contraseña temporal al email del contacto.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)} className="rounded-none">Cancelar</Button>
            <Button onClick={handleCreate} className="rounded-none bg-[#002FA7] hover:bg-[#001D66]" data-testid="confirm-create-client">Crear cliente</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="rounded-none max-w-md">
          <DialogHeader><DialogTitle className="font-heading">Editar cliente</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-4 py-2">
              <p className="text-sm font-medium">{editing.company_name}</p>
              <div><Label className="text-xs font-mono uppercase">Plan</Label>
                <Select value={editing.plan_id} onValueChange={(v) => setEditing({...editing, plan_id: v})}>
                  <SelectTrigger className="rounded-none mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {plans.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs font-mono uppercase">Ciclo de facturación</Label>
                <Select value={editing.billing_cycle} onValueChange={(v) => setEditing({...editing, billing_cycle: v})}>
                  <SelectTrigger className="rounded-none mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="monthly">Mensual</SelectItem>
                    <SelectItem value="annual">Anual</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center justify-between pt-2 border-t border-border/40">
                <Label>Cuenta activa</Label>
                <Switch checked={editing.is_active} onCheckedChange={(v) => setEditing({...editing, is_active: v})} />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)} className="rounded-none">Cancelar</Button>
            <Button onClick={handleEdit} className="rounded-none bg-[#002FA7] hover:bg-[#001D66]">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
